/**
 * Cache-Service für die Anwendung
 * Ermöglicht das Speichern und Abrufen von Daten mit definierter Lebensdauer
 */

// Standard-Cache-Lebensdauer in Millisekunden (10 Minuten)
const DEFAULT_CACHE_LIFETIME = 10 * 60 * 1000;

class CacheService {
  constructor() {
    // Cache-Speicher mit Zeitstempeln
    this.cache = new Map();
    // Versionsverwaltung pro Entitätstyp
    this.versions = new Map();
  }

  /**
   * Speichert Daten im Cache
   * @param {string} key - Eindeutiger Schlüssel für die Daten
   * @param {any} data - Die zu cachenden Daten
   * @param {number} lifetime - Optionale Lebensdauer in Millisekunden
   */
  set(key, data, lifetime = DEFAULT_CACHE_LIFETIME) {
    const timestamp = Date.now();
    const expiry = timestamp + lifetime;
    
    this.cache.set(key, {
      data,
      timestamp,
      expiry
    });
    
    console.log(`Cache: Daten wurden gecached für Schlüssel "${key}"`);
    return data;
  }

  /**
   * Ruft Daten aus dem Cache ab
   * @param {string} key - Schlüssel der Daten
   * @param {boolean} checkExpiry - Prüft, ob die Daten abgelaufen sind
   * @returns {any|null} Die Daten oder null, wenn nicht gefunden oder abgelaufen
   */
  get(key, checkExpiry = true) {
    if (!this.cache.has(key)) {
      return null;
    }

    const cachedItem = this.cache.get(key);
    
    // Prüfen, ob die Daten abgelaufen sind
    if (checkExpiry && cachedItem.expiry < Date.now()) {
      console.log(`Cache: Daten für Schlüssel "${key}" sind abgelaufen`);
      this.cache.delete(key);
      return null;
    }
    
    console.log(`Cache: Daten wurden aus dem Cache geladen für Schlüssel "${key}"`);
    return cachedItem.data;
  }

  /**
   * Prüft, ob Daten im Cache vorhanden und nicht abgelaufen sind
   * @param {string} key - Der zu prüfende Schlüssel
   * @returns {boolean} true, wenn gültige Daten im Cache sind
   */
  has(key) {
    if (!this.cache.has(key)) {
      return false;
    }
    
    const cachedItem = this.cache.get(key);
    return cachedItem.expiry >= Date.now();
  }

  /**
   * Löscht einen bestimmten Eintrag aus dem Cache
   * @param {string} key - Der zu löschende Schlüssel
   */
  remove(key) {
    this.cache.delete(key);
    console.log(`Cache: Eintrag "${key}" wurde aus dem Cache entfernt`);
  }

  /**
   * Löscht alle Einträge, die zu einem Entitätstyp gehören
   * @param {string} entityType - Der Entitätstyp (z.B. 'tests', 'questions')
   */
  invalidateEntityType(entityType) {
    // Inkrementiere die Version für diesen Entitätstyp
    const currentVersion = this.versions.get(entityType) || 0;
    this.versions.set(entityType, currentVersion + 1);
    
    // Lösche alle Einträge, die mit diesem Entitätstyp beginnen
    for (const key of this.cache.keys()) {
      if (key.startsWith(`${entityType}:`)) {
        this.cache.delete(key);
      }
    }
    
    console.log(`Cache: Alle Einträge für Entitätstyp "${entityType}" wurden invalidiert`);
  }

  /**
   * Erstellt einen Schlüssel für eine Entität
   * @param {string} entityType - Der Entitätstyp (z.B. 'tests', 'questions')
   * @param {string|number} id - Optional: ID der Entität
   * @param {string} subResource - Optional: Unterressource (z.B. 'questions')
   * @returns {string} Der generierte Schlüssel
   */
  createKey(entityType, id = null, subResource = null) {
    let key = entityType;
    
    // Füge Version hinzu, um bei Invalidierung alle Einträge zu erneuern
    const version = this.versions.get(entityType) || 0;
    key += `:v${version}`;
    
    if (id !== null) {
      key += `:${id}`;
      
      if (subResource !== null) {
        key += `:${subResource}`;
      }
    }
    
    return key;
  }

  /**
   * Löscht alle abgelaufenen Einträge aus dem Cache
   */
  cleanup() {
    const now = Date.now();
    let removedCount = 0;
    
    for (const [key, value] of this.cache.entries()) {
      if (value.expiry < now) {
        this.cache.delete(key);
        removedCount++;
      }
    }
    
    if (removedCount > 0) {
      console.log(`Cache: ${removedCount} abgelaufene Einträge wurden entfernt`);
    }
  }

  /**
   * Löscht den gesamten Cache
   */
  clear() {
    this.cache.clear();
    console.log('Cache: Alle Einträge wurden gelöscht');
  }

  /**
   * Invalidiert einen bestimmten Entity-Eintrag im Cache
   * @param {string} entityType - Typ der Entität (z.B. 'applicants', 'tests')
   * @param {string|number} id - ID der Entität
   */
  invalidateEntity(entityType, id) {
    if (!entityType || !id) {
      console.warn('Cache: Ungültige Parameter für invalidateEntity', { entityType, id });
      return;
    }
    
    // Schlüssel für die spezifische Entität erstellen
    const key = this.createKey(entityType, id);
    
    // Cache-Eintrag entfernen
    if (this.cache.has(key)) {
      this.cache.delete(key);
      console.log(`Cache: Eintrag für ${entityType} mit ID ${id} wurde invalidiert`);
    }
    
    // Auch den Eintrag für die Gesamtliste invalidieren, da sich diese geändert haben könnte
    this.invalidateEntityType(entityType);
  }
}

// Erstelle eine globale Instanz des Cache-Services
const cacheService = new CacheService();

// Starte automatische Bereinigung alle 5 Minuten
setInterval(() => {
  cacheService.cleanup();
}, 5 * 60 * 1000);

export default cacheService; 